// Shared services cache — eliminates the "Loading…" flash when navigating to
// /services/:slug after the services list has been fetched at least once
// during the session. Cache lives in sessionStorage, so it survives within
// the same tab session but doesn't go stale across days.
import { useEffect, useState } from "react";
import api from "./api";

const KEY = "lh2o_services_cache_v1";
let memoryCache = null;
let inFlight = null;

function readSession() {
  try {
    const raw = sessionStorage.getItem(KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
}

function writeSession(data) {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(data));
  } catch (_) {
    /* quota / private mode — ignore */
  }
}

export async function fetchServices() {
  if (inFlight) return inFlight;
  inFlight = api
    .get("/services")
    .then((r) => {
      memoryCache = r.data;
      writeSession(r.data);
      return r.data;
    })
    .finally(() => {
      inFlight = null;
    });
  return inFlight;
}

/**
 * Returns the cached services list (sync from session) and silently
 * revalidates in the background.
 *
 * On first ever visit it returns `null` so the consumer can render a
 * skeleton; the resolver hook will replace it with real data when fetched.
 */
export function useServices() {
  const cached = memoryCache || readSession();
  const [services, setServices] = useState(cached);

  useEffect(() => {
    let alive = true;
    fetchServices()
      .then((data) => {
        if (alive) setServices(data);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  return services;
}
